import { Injectable, NgZone, inject } from '@angular/core';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { Router } from '@angular/router';
import { NotificationFacade } from '@core/facades/notification.facade';
import {
  TableStateModel,
  ApprovalStateModel,
  ITableStateModel,
  ITableFilter,
} from './table.state.model';
import { ApprovalService } from '@core/services/approval.service';
import { Pagination } from '@core/models/pagination.model';
import { TableActions } from './table.actions';
import { AccountManagementService } from '@core/services/account-management.service';
import { UserService } from '@core/services/user.service';
import { AdminManagementService } from '@core/services/admin-management.service';
import { RolesAndPermissionsService } from '@core/services/roles-and-permissions.service';
import { OrderService } from '@core/services/order.service';
import { MutualFundServiceV2 } from '@core/services/mutual-fund.service-v2';
import { catchError, take, tap, throwError } from 'rxjs';
import { ReportService } from '@core/services/report.service';
import { AccountsHandler } from './table-handlers/account-list';
import { MFProductHandler } from './table-handlers/mf-product';
import { LCMProductHandler } from './table-handlers/lcm-products';
import { TransactionsHandler } from './table-handlers/transactions';
import { ReferralsHandler } from './table-handlers/referrals';
import { RolesHandler } from './table-handlers/roles';
import { MutualFundService } from '@core/services/mutual-fund.service';
import { IdentityHandler } from './table-handlers/identity';
import { UpdateApprovalsHandler } from './table-handlers/update-approvals';
import { AuditHandler } from './table-handlers/audit';
import { OrderApprovalsHandler } from './table-handlers/order-approvals';
import { AccountsRMHandler } from './table-handlers/accounts-rm';
import { ConfigurationsHandler } from './table-handlers/configurations';
import { ReportsHandler } from './table-handlers/reports';
import { BanksHandler } from './table-handlers/banks';
import { FacialRecognitionLogHandler } from './table-handlers/facial-recognition-log';
import { NotificationLogsHandler } from './table-handlers/notification-logs';

@State<TableStateModel>({
  name: 'table',
  defaults: {
    // loading: false,
    approval: {
      ...getDefaultTableState(),
      approvals: [],
    },
    accounts: {
      ...getDefaultTableState(),
      accountMetadata: null,
    },
    accountsRM: {
      ...getDefaultTableState(),
      accountRMMetadata: null,
    },
    mobileUsers: {
      ...getDefaultTableState(),
    },
    systemUsers: {
      ...getDefaultTableState(),
      roles: [],
    },
    roles: {
      ...getDefaultTableState(),
      permissions: [],
    },
    orders: {
      ...getDefaultTableState(),
    },
    activeClients: {
      ...getDefaultTableState(),
    },
    inactiveClients: {
      ...getDefaultTableState(),
    },
    portfolio: {
      ...getDefaultTableState(),
    },
    general: {
      ...getDefaultTableState(),
    },
    clientWF: {
      ...getDefaultTableState(),
    },
    mutualFunds: {
      ...getDefaultTableState(),
    },
    fixedDeposits: {
      ...getDefaultTableState(),
    },
    tbills: {
      ...getDefaultTableState(),
    },
    bonds: {
      ...getDefaultTableState(),
    },
    walletTransactions: {
      ...getDefaultTableState(),
      providusBalance: 0,
      accessBankBalance: 0,
      vertoBalance: 0,
      paystackBalance: 0,
    },
    fxTransactions: {
      ...getDefaultTableState(),
      totalUSD: 0,
      totalNGN: 0,
      dayTotalUSD: 0,
      dayTotalNgn: 0,
    },
    notificationLogs: {
      ...getDefaultTableState(),
    },
    cashTransactions: {
      ...getDefaultTableState(),
      investmentCash: 0,
      walletCash: 0,
    },
    referrals: {
      ...getDefaultTableState(),
      referralMetadata: null,
    },
    identityLog: {
      ...getDefaultTableState(),
    },
    updateApprovals: {
      ...getDefaultTableState(),
      updatesMetadata: null,
    },
    auditLog: {
      ...getDefaultTableState(),
    },
    orderApprovals: {
      ...getDefaultTableState(),
    },
    configurations: {
      ...getDefaultTableState(),
    },
    mnmmfStatements: {
      ...getDefaultTableState(),
    },
    banks: {
      ...getDefaultTableState(),
    },
    benchmark: {
      ...getDefaultTableState(),
    },
    facialRecognitionLog: {
      ...getDefaultTableState(),
    },
    notificationUsers: {
      ...getDefaultTableState(),
    },

  },
})
@Injectable()
export class TableState {
  approvalService = inject(ApprovalService);
  store = inject(Store);
  ngZone = inject(NgZone);
  router = inject(Router);
  toast = inject(NotificationFacade);
  accountService = inject(AccountManagementService);
  userService = inject(UserService);
  adminService = inject(AdminManagementService);
  roleService = inject(RolesAndPermissionsService);
  notify = inject(NotificationFacade);
  rolePermissionService = inject(RolesAndPermissionsService);
  orderService = inject(OrderService);
  mfService = inject(MutualFundServiceV2);
  mf1Service = inject(MutualFundService);
  reportService = inject(ReportService);

  // Inject the handler for each table
  accountsHandler = inject(AccountsHandler);
  accountsRMHandler = inject(AccountsRMHandler);
  mfHandler = inject(MFProductHandler);
  lcmProductHandler = inject(LCMProductHandler);
  transactionsHandler = inject(TransactionsHandler);
  referralsHandler = inject(ReferralsHandler);
  rolesHandler = inject(RolesHandler);
  identityHandler = inject(IdentityHandler);
  updateApprovalsHandler = inject(UpdateApprovalsHandler);
  auditHandler = inject(AuditHandler);
  orderApprovalsHandler = inject(OrderApprovalsHandler);
  configurationsHandler = inject(ConfigurationsHandler);
  reportHandler = inject(ReportsHandler);
  banksHandler = inject(BanksHandler);
  facialRecognitionLogHandler = inject(FacialRecognitionLogHandler);
  notificationLogsHandler = inject(NotificationLogsHandler);

  constructor() {}

  // Generic helper method to get table state
  private getTableState(
    state: TableStateModel,
    tableKey: string
  ): ITableStateModel {
    switch (tableKey) {
      case 'approval':
        return state.approval;
      case 'accounts':
        return state.accounts || getDefaultTableState();
      case 'accountsRM':
        return state.accountsRM || getDefaultTableState();
      case 'mobileUsers':
        return state.mobileUsers || getDefaultTableState();
      case 'systemUsers':
        return state.systemUsers || getDefaultTableState();
      case 'roles':
        return state.roles || getDefaultTableState();
      case 'orders':
        return state.orders || getDefaultTableState();
      case 'clientWF':
        return state.clientWF || getDefaultTableState();
      case 'activeClients':
        return state.activeClients || getDefaultTableState();
      case 'inactiveClients':
        return state.inactiveClients || getDefaultTableState();
      case 'portfolio':
        return state.portfolio || getDefaultTableState();
      case 'general':
        return state.general || getDefaultTableState();
      case 'mutualFunds':
        return state.mutualFunds || getDefaultTableState();
      case 'fixedDeposits':
        return state.fixedDeposits || getDefaultTableState();
      case 'tbills':
        return state.tbills || getDefaultTableState();
      case 'bonds':
        return state.bonds || getDefaultTableState();
      case 'commercialPapers':
        return state.commercialPapers || getDefaultTableState();
      case 'pmsProducts':
        return state.pmsProducts || getDefaultTableState();
      case 'walletTransactions':
        return state.walletTransactions || getDefaultTableState();
      case 'fxTransactions':
        return state.fxTransactions || getDefaultTableState();
      case 'cashTransactions':
        return state.cashTransactions || getDefaultTableState();
      case 'referrals':
        return state.referrals || getDefaultTableState();
      case 'identityLog':
        return state.identityLog || getDefaultTableState();
      case 'updateApprovals':
        return state.updateApprovals || getDefaultTableState();
      case 'auditLog':
        return state.auditLog || getDefaultTableState();
      case 'orderApprovals':
        return state.orderApprovals || getDefaultTableState();
      case 'relationshipManagers':
        return state.relationshipManagers || getDefaultTableState();
      case 'configurations':
        return state.configurations || getDefaultTableState();
      case 'mnmmfStatements':
        return state.mnmmfStatements || getDefaultTableState();
      case 'banks':
        return state.banks || getDefaultTableState();
      case 'benchmark':
        return state.benchmark || getDefaultTableState();
      case 'facialRecognitionLog':
        return state.facialRecognitionLog || getDefaultTableState();
      case 'notificationLogs':
        return state.notificationLogs || getDefaultTableState();
      case 'notificationUsers':
        return state.notificationUsers || getDefaultTableState();
      default:
        throw new Error(`Unknown table key: ${tableKey}`);
    }
  }

  // Generic table actions
  @Action(TableActions.SetTableFilters)
  setTableFilters(
    ctx: StateContext<TableStateModel>,
    action: TableActions.SetTableFilters
  ) {
    const state = ctx.getState();
    const currentTableState = this.getTableState(
      state,
      action.payload.tableKey
    );

    const filtersObject = action.payload.filters.reduce(
      (acc, filter) => {
        acc[filter.key] = filter.value;
        return acc;
      },
      {} as Record<string, any>
    );

    ctx.patchState({
      [action.payload.tableKey]: {
        ...currentTableState,
        filters: filtersObject,
      },
    } as Partial<TableStateModel>);
  }

  // Generic handler for all table data loading
  @Action(TableActions.LoadTableData)
  loadTableData(
    ctx: StateContext<TableStateModel>,
    action: TableActions.LoadTableData
  ) {
    const { tableKey, params, filters } = action.payload;
    const currentTableState = this.getTableState(ctx.getState(), tableKey);

    ctx.patchState({
      [tableKey]: {
        ...currentTableState,
        loading: true,
      },
    } as Partial<TableStateModel>);

    // Route to appropriate service based on tableKey
    switch (tableKey) {
      //  case 'approval':
      //    this.handleApprovalLoad(ctx, params);
      //    break;
      case 'accounts':
        this.accountsHandler.handleAccountsLoad(ctx, params);
        break;
      case 'accountsRM':
        this.accountsRMHandler.handleAccountsRMLoad(ctx, params);
        break;
      case 'mobileUsers':
        this.handleMobileUsersLoad(ctx, params);
        break;
      case 'systemUsers':
        this.handleSystemUsersLoad(ctx, params);
        break;
      case 'roles':
        this.handleRolesTableLoad(ctx, params);
        break;
      case 'orders':
        this.handleOrdersLoad(ctx, params);
        break;
      case 'inactiveClients':
        this.handleInactiveClientsLoad(ctx, params);
        break;
      case 'activeClients':
        this.handleActiveClientLoad(ctx, params);
        break;
      case 'clientWF':
        this.handleClientWFLoad(ctx, params);
        break;
      case 'portfolio':
        this.handlePortfolioLoad(ctx, params);
        break;
      case 'general':
        this.handleGeneralLoad(ctx, params);
        break;
      case 'mutualFunds':
        this.mfHandler.handleMFProductLoad(ctx, params);
        break;
      case 'fixedDeposits':
        this.lcmProductHandler.handleFDProductLoad(ctx, params);
        break;
      case 'tbills':
        this.lcmProductHandler.handleTBillProductLoad(ctx, params);
        break;
      case 'bonds':
        this.lcmProductHandler.handleBondsProductLoad(ctx, params);
        break;
      case 'commercialPapers':
        this.lcmProductHandler.handleCPaperProductLoad(ctx, params);
        break;
      case 'pmsProducts':
        this.lcmProductHandler.handlePMSProductLoad(ctx, params);
        break;
      case 'walletTransactions':
        this.transactionsHandler.handleWalletTransactionLoad(ctx, params);
        break;
      case 'fxTransactions':
        this.transactionsHandler.handleFxTransactionLoad(ctx, params);
        break;
      case 'cashTransactions':
        this.transactionsHandler.handleCashTransactionLoad(ctx, params);
        break;
      case 'referrals':
        this.referralsHandler.handleReferralsLoad(ctx, params);
        break;
      case 'identityLog':
        this.identityHandler.handleIdentityLogsLoad(ctx, params);
        break;
      case 'updateApprovals':
        this.updateApprovalsHandler.handleUpdateApprovalsLoad(ctx, params);
        break;
      case 'auditLog':
        this.auditHandler.handleAuditLogsLoad(ctx, params);
        break;
      case 'orderApprovals':
        this.orderApprovalsHandler.handleOrderApprovalsLoad(ctx, params);
        break;
      case 'relationshipManagers':
        this.accountsRMHandler.handleRelationshipManagersLoad(ctx, params);
        break;
      case 'configurations':
        this.configurationsHandler.handleConfigurationsLoad(ctx, params);
        break;
      case 'mnmmfStatements':
        this.reportHandler.handleMnmmfStatementsLoad(ctx, params);
        break;
      case 'banks':
        this.banksHandler.handleBanksLoad(ctx, params);
        break;
      case 'benchmark':
        this.mfHandler.handleBenchmarkLoad(ctx, params);
        break;
      case 'facialRecognitionLog':
        this.facialRecognitionLogHandler.handleFacialRecognitionLogsLoad(ctx, params);
        break;
      case 'notificationLogs':
        this.notificationLogsHandler.handleNotificationLogsLoad(ctx, params);
        break;
      case 'notificationUsers':
        this.notificationLogsHandler.handleNotificationUsersLoad(ctx, params);
        break;
      // Add other tables as needed
    }
  }

  // Generic success handler works for all tables
  @Action(TableActions.LoadTableDataSuccess)
  loadTableDataSuccess(
    ctx: StateContext<TableStateModel>,
    action: TableActions.LoadTableDataSuccess
  ) {
    const { tableKey, data, totalCount } = action.payload;
    const currentTableState = this.getTableState(ctx.getState(), tableKey);

    ctx.patchState({
      [tableKey]: {
        ...currentTableState,
        data,
        totalCount: totalCount || data.length,
        loading: false,
        error: null,
      },
    } as Partial<TableStateModel>);
  }

  //  approvals
  @Action(TableActions.LoadApprovals)
  loadApprovals(
    ctx: StateContext<TableStateModel>,
    action: TableActions.LoadApprovals
  ) {
    ctx.patchState({
      approval: {
        ...ctx.getState().approval,
        loading: true,
      },
    });

    this.approvalService.getDocumentsList(action.payload.params).subscribe({
      next: (response: any) => {
        ctx.dispatch(
          new TableActions.LoadApprovalsSuccess({
            approvals: response.data?.data || response,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadApprovalsFailure({
            error: error.message,
          })
        );
      },
    });
  }
  @Action(TableActions.LoadApprovalsSuccess)
  loadApprovalsSuccess(
    ctx: StateContext<TableStateModel>,
    action: TableActions.LoadApprovalsSuccess
  ) {
    ctx.patchState({
      approval: {
        ...ctx.getState().approval,
        data: action.payload.approvals,
        approvals: action.payload.approvals,
        loading: false,
        error: null,
      },
    });
  }

  @Action(TableActions.LoadApprovalsFailure)
  loadApprovalsFailure(
    ctx: StateContext<TableStateModel>,
    action: TableActions.LoadApprovalsFailure
  ) {
    ctx.patchState({
      approval: {
        ...ctx.getState().approval,
        loading: false,
        error: action.payload.error,
      },
    });
  }

  @Action(TableActions.UpdateDocumentStatus)
  updateDocumentStatus(
    ctx: StateContext<TableStateModel>,
    action: TableActions.UpdateDocumentStatus
  ) {
    const state = ctx.getState();
    const updatedApprovals = state.approval.approvals.map((doc) => {
      if (doc.id === action.payload.documentId) {
        return { ...doc, status: action.payload.status };
      }
      return doc;
    });

    ctx.patchState({
      approval: {
        ...state.approval,
        data: updatedApprovals,
        approvals: updatedApprovals,
      },
    });
  }

  @Action(TableActions.ApproveApproval)
  approveApproval(
    ctx: StateContext<TableStateModel>,
    action: TableActions.ApproveApproval
  ) {
    this.approvalService.approveDocument(action.payload.approvalId).subscribe({
      next: () => {
        const state = ctx.getState();
        const approvals = state.approval.approvals.map((approval) =>
          approval.id === action.payload.approvalId
            ? { ...approval, status: 'approved' }
            : approval
        );

        ctx.patchState({
          approval: {
            ...state.approval,
            data: approvals,
            approvals,
          },
        });
      },
      error: (error) => {
        console.error('Failed to approve approval:', error);
      },
    });
  }

  @Action(TableActions.RejectApproval)
  rejectApproval(
    ctx: StateContext<TableStateModel>,
    action: TableActions.RejectApproval
  ) {
    this.approvalService.rejectDocument(action.payload.approvalId).subscribe({
      next: () => {
        const state = ctx.getState();
        const approvals = state.approval.approvals.map((approval) =>
          approval.id === action.payload.approvalId
            ? { ...approval, status: 'rejected' }
            : approval
        );

        ctx.patchState({
          approval: {
            ...state.approval,
            data: approvals,
            approvals,
          },
        });
      },
      error: (error) => {
        console.error('Failed to reject approval:', error);
      },
    });
  }

  // accounts list

  @Action(TableActions.GetAccountsMetadata)
  getAccountsMetadata(ctx: StateContext<TableStateModel>) {
    this.accountsHandler.handleGetMetadata(ctx);
  }

  @Action(TableActions.GetAccountsMetadataSuccess)
  getAccountsMetadataSuccess(
    ctx: StateContext<TableStateModel>,
    action: TableActions.GetAccountsMetadataSuccess
  ) {
    this.accountsHandler.getAccountsMetadataSuccess(ctx, action);
  }

  @Action(TableActions.GetAccountsMetadataFailure)
  getAccountsMetadataFailure(
    ctx: StateContext<TableStateModel>,
    action: TableActions.GetAccountsMetadataFailure
  ) {
    this.accountsHandler.getAccountsMetadataFailure(ctx, action);
  }

  @Action(TableActions.FilterAccountList)
  filterAccountList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterAccountList
  ) {
    this.accountsHandler.filterAccountList(ctx, action);
  }

  @Action(TableActions.BlockAccount)
  blockAccount(
    ctx: StateContext<TableStateModel>,
    action: TableActions.BlockAccount
  ) {
    this.accountsHandler.blockAccount(ctx, action);
  }

  @Action(TableActions.UnblockAccount)
  unblockAccount(
    ctx: StateContext<TableStateModel>,
    action: TableActions.UnblockAccount
  ) {
    this.accountsHandler.unblockAccount(ctx, action);
  }

  @Action(TableActions.DeleteAccount)
  deleteAccount(
    ctx: StateContext<TableStateModel>,
    action: TableActions.DeleteAccount
  ) {
    this.accountsHandler.deleteAccount(ctx, action);
  }

  // rm

  @Action(TableActions.GetAccountsRMMetadata)
  getAccountsRMMetadata(ctx: StateContext<TableStateModel>) {
    this.accountsRMHandler.handleGetMetadata(ctx);
  }

  @Action(TableActions.GetAccountsMetadataSuccess)
  getAccountsRMMetadataSuccess(
    ctx: StateContext<TableStateModel>,
    action: TableActions.GetAccountsRMMetadataSuccess
  ) {
    this.accountsRMHandler.getAccountsMetadataSuccess(ctx, action);
  }

  @Action(TableActions.GetAccountsRMMetadataFailure)
  getAccountsRMMetadataFailure(
    ctx: StateContext<TableStateModel>,
    action: TableActions.GetAccountsRMMetadataFailure
  ) {
    this.accountsRMHandler.getAccountsMetadataFailure(ctx, action);
  }

  @Action(TableActions.FilterAccountRMList)
  filterAccountRMList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterAccountRMList
  ) {
    this.accountsRMHandler.filterAccountRMList(ctx, action);
  }

  // mobile app user list
  private handleMobileUsersLoad(
    ctx: StateContext<TableStateModel>,
    params: Pagination
  ) {
    this.userService.viewProfileList({ ...params }).subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.LoadTableDataSuccess({
            tableKey: 'mobileUsers',
            data: response?.data,
            totalCount: response?.total_count,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadTableDataFailure({
            tableKey: 'mobileUsers',
            error: error.message,
          })
        );
      },
    });
  }

  @Action(TableActions.FilterMobileUsers)
  filterMobileUsers(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterMobileUsers
  ) {
    return this.userService.filterProfileList(action.payload.filter).subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.LoadTableDataSuccess({
            tableKey: 'mobileUsers',
            data: response?.data,
            totalCount: response?.total_count,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadTableDataFailure({
            tableKey: 'mobileUsers',
            error: error.message,
          })
        );
      },
    });
  }

  // system user list
  private handleSystemUsersLoad(
    ctx: StateContext<TableStateModel>,
    params: Pagination
  ) {
    this.adminService.viewAdminList({ ...params }).subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.LoadTableDataSuccess({
            tableKey: 'systemUsers',
            data: response.data?.data,
            totalCount: response.data?.total_count,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadTableDataFailure({
            tableKey: 'systemUsers',
            error: error.message,
          })
        );
      },
    });
  }

  @Action(TableActions.GetSystemUsersRoles)
  getSystemUserRoles(ctx: StateContext<TableStateModel>) {
    return this.roleService.getAllRoles().subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.GetSystemUsersRolesSuccess({
            roles: response.data?.data,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.GetSystemUsersRolesFailure({
            error: error.message,
          })
        );
      },
    });
  }

  @Action(TableActions.GetSystemUsersRolesSuccess)
  getSystemUserRolesSuccess(
    ctx: StateContext<TableStateModel>,
    action: TableActions.GetSystemUsersRolesSuccess
  ) {
    const currentState = ctx.getState();

    const currentUsers = currentState.systemUsers || getDefaultTableState();
    ctx.patchState({
      systemUsers: {
        ...currentUsers,
        roles: action.payload.roles,
      },
    });
  }

  @Action(TableActions.GetSystemUsersRolesFailure)
  getSystemUserRolesFailure(
    ctx: StateContext<TableStateModel>,
    action: TableActions.GetAccountsMetadataFailure
  ) {
    const currentState = ctx.getState();
    const currentUsers = currentState.systemUsers || getDefaultTableState();

    ctx.patchState({
      systemUsers: {
        ...currentUsers,
        error: action.payload.error,
      },
    });
  }

  @Action(TableActions.FilterSystemUsers)
  filterSystemUsers(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterSystemUsers
  ) {
    return this.adminService.fitlerAdminList(action.payload.filter).subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.LoadTableDataSuccess({
            tableKey: 'systemUsers',
            data: response.data?.data,
            totalCount: response.data?.total_count,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadTableDataFailure({
            tableKey: 'systemUsers',
            error: error.message,
          })
        );
      },
    });
  }

  // roles table
  private handleRolesTableLoad(
    ctx: StateContext<TableStateModel>,
    params: Pagination
  ) {
    this.rolesHandler.handleRolesLoad(ctx, params);
  }

  @Action(TableActions.DeleteRole)
  deleteRole(
    ctx: StateContext<TableStateModel>,
    action: TableActions.DeleteRole
  ) {
    this.rolesHandler.deleteRole(ctx, action);
  }

  @Action(TableActions.FilterRoleList)
  filterRolesList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterRoleList
  ) {
    this.rolesHandler.filterRolesList(ctx, action);
  }

  @Action(TableActions.GetPermissions)
  getPermissions(ctx: StateContext<TableStateModel>) {
    return this.rolePermissionService.getAllPermissions().pipe(
      take(1),
      tap((response) => {
        const state = ctx.getState();
        ctx.patchState({
          roles: {
            ...state.roles,
            permissions: response?.data,
          },
        } as Partial<TableStateModel>);
      })
    );
  }

  @Action(TableActions.UpdateRolePermission)
  updateRolePermission(
    ctx: StateContext<TableStateModel>,
    action: TableActions.UpdateRolePermission
  ) {
    const state = ctx.getState();
    const updatedRoles = state.roles?.data.map((role) => {
      if (role.id === action.payload.roleId) {
        const updatedRole = { ...role };
        const permissionToToggle = action.payload.permission;
        const permissionId = permissionToToggle.id;

        if (action.payload.isAdd) {
          const alreadyHas = updatedRole.permissions.some(
            (p: any) => p.permission?.id === permissionId
          );
          if (!alreadyHas) {
            updatedRole.permissions = [
              ...updatedRole.permissions,
              { permission: permissionToToggle },
            ];
          }
        } else {
          updatedRole.permissions = updatedRole.permissions.filter(
            (p: any) => p.permission?.id !== permissionId
          );
        }
        return updatedRole;
      }
      return role;
    });

    // Patch the state with the new roles array
    ctx.patchState({
      roles: {
        ...state.roles,
        data: updatedRoles,
      },
    } as Partial<TableStateModel>);
  }

  // orders
  private handleOrdersLoad(
    ctx: StateContext<TableStateModel>,
    params: Pagination
  ) {
    this.orderService.viewOrderList({ ...params }).subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.LoadTableDataSuccess({
            tableKey: 'orders',
            data: response.data?.data,
            totalCount: response.data?.total_count,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadTableDataFailure({
            tableKey: 'orders',
            error: error.message,
          })
        );
      },
    });
  }

  @Action(TableActions.FilterOrderList)
  filterOrderList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterOrderList
  ) {
    return this.orderService.filterOrders(action.payload.filter).subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.LoadTableDataSuccess({
            tableKey: 'orders',
            data: response.data?.data,
            totalCount: response.data?.total_count,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadTableDataFailure({
            tableKey: 'orders',
            error: error.message,
          })
        );
      },
    });
  }

  @Action(TableActions.ApproveOrder)
  approveOrder(
    ctx: StateContext<TableStateModel>,
    action: TableActions.ApproveOrder
  ) {
    return this.orderService
      .approveOrder(
        action.payload.orderId,
        'completed',
        action.payload?.value_date
      )
      .pipe(
        tap(() => {
          const state = ctx.getState();
          const updatedOrders = state.orders?.data.map((order) =>
            order.id === action.payload.orderId
              ? { ...order, status: 'completed' }
              : order
          );

          ctx.patchState({
            orders: {
              ...state.orders,
              data: updatedOrders,
            },
          } as Partial<TableStateModel>);
        }),
        catchError((error) => {
          console.error('Failed to approve order:', error);
          // You can dispatch an error action here if needed
          return throwError(() => error);
        })
      );
  }
  @Action(TableActions.ApproveMFTransaction)
  approveMFTransaction(
    ctx: StateContext<TableStateModel>,
    action: TableActions.ApproveMFTransaction
  ) {
    return this.mf1Service
      .approveTransaction(action.payload.orderId, action.payload?.value_date)
      .pipe(
        tap((resp) => {
          this.notify.success(resp.message);
          const state = ctx.getState();
          const updatedOrders = state.orders?.data.map((order) =>
            order.id === action.payload.orderId
              ? { ...order, status: 'completed' }
              : order
          );

          ctx.patchState({
            orders: {
              ...state.orders,
              data: updatedOrders,
            },
          } as Partial<TableStateModel>);
        }),
        catchError((error) => {
          console.error('Failed to approve MF transaction:', error);
          this.notify.error('Failed to approve transaction');
          return throwError(() => error);
        })
      );
  }

  @Action(TableActions.RejectOrder)
  rejectOrder(
    ctx: StateContext<TableStateModel>,
    action: TableActions.RejectOrder
  ) {
    return this.orderService
      .rejectOrder(action.payload.orderId, 'terminated')
      .pipe(
        tap(() => {
          const state = ctx.getState();
          const updatedOrders = state.orders?.data.map((order) =>
            order.id === action.payload.orderId
              ? { ...order, status: 'terminated' }
              : order
          );

          ctx.patchState({
            orders: {
              ...state.orders,
              data: updatedOrders,
            },
          } as Partial<TableStateModel>);
        }),
        catchError((error) => {
          console.error('Failed to reject order:', error);
          this.notify.error('Failed to reject order');
          return throwError(() => error);
        })
      );
  }

  @Action(TableActions.RejectMFTransaction)
  rejectMFTransaction(
    ctx: StateContext<TableStateModel>,
    action: TableActions.RejectMFTransaction
  ) {
    return this.mf1Service.rejectTransaction(action.payload.orderId).pipe(
      tap((resp) => {
        this.notify.success(resp.message);
        const state = ctx.getState();
        const updatedOrders = state.orders?.data.map((order) =>
          order.id === action.payload.orderId
            ? { ...order, status: 'terminated' }
            : order
        );

        ctx.patchState({
          orders: {
            ...state.orders,
            data: updatedOrders,
          },
        } as Partial<TableStateModel>);
      }),
      catchError((error) => {
        console.error('Failed to reject MF transaction:', error);
        this.notify.error('Failed to reject transaction');
        return throwError(() => error);
      })
    );
  }

  // Reports
  // Active client
  private handleActiveClientLoad(
    ctx: StateContext<TableStateModel>,
    params: Pagination
  ) {
    this.reportHandler.handleActiveClientLoad(ctx, params);
  }

  @Action(TableActions.FilterActiveClientsList)
  filterActiveClientsList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterActiveClientsList
  ) {
    this.reportHandler.filterActiveClientsList(ctx, action);
  }

  // Inactive client
  private handleInactiveClientsLoad(
    ctx: StateContext<TableStateModel>,
    params: Pagination
  ) {
    this.reportHandler.handleInactiveClientsLoad(ctx, params);
  }
  // Client with fund
  private handleClientWFLoad(
    ctx: StateContext<TableStateModel>,
    params: Pagination
  ) {
    this.reportHandler.handleClientWFLoad(ctx, params);
  }

  @Action(TableActions.FilterFundedClientsList)
  filterFundedClientsList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterFundedClientsList
  ) {
    this.reportHandler.filterFundedClientsList(ctx, action);
  }

  // portfolio
  private handlePortfolioLoad(
    ctx: StateContext<TableStateModel>,
    params: Pagination
  ) {
    this.reportHandler.handlePortfolioLoad(ctx, params);
  }

  @Action(TableActions.FilterPortfolioReportList)
  filterPortfolioReportList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterPortfolioReportList
  ) {
    this.reportHandler.filterPortfolioReportList(ctx, action);
  }

  // General
  private handleGeneralLoad(
    ctx: StateContext<TableStateModel>,
    params: Pagination
  ) {
    this.reportHandler.handleGeneralLoad(ctx, params);
  }

  // MNMMF Statements
  @Action(TableActions.FilterMNMMFStatementsList)
  filterMnmmfStatementsList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterMNMMFStatementsList
  ) {
    this.reportHandler.filterMNMMFStatementsList(ctx, action);
  }

  // Benchmark
  @Action(TableActions.FilterBenchmarkList)
  filterBenchmarkList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterBenchmarkList
  ) {
    this.mfHandler.handleBenchmarkFilter(ctx, action);
  }

  // Facial Recognition Log
  @Action(TableActions.FilterFacialRecognitionLogList)
  filterFacialRecognitionLogList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterFacialRecognitionLogList
  ) {
    return this.facialRecognitionLogHandler.filterFacialRecognitionLogList(ctx, action);
  }

  // Notification Logs
  @Action(TableActions.FilterNotificationLog)
  filterNotificationLog(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterNotificationLog
  ) {
    return this.notificationLogsHandler.filterNotificationLogList(ctx, action);
  }

  @Action(TableActions.FilterNotificationUsersList)
  filterNotificationUsersList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterNotificationUsersList
  ) {
    return this.notificationLogsHandler.filterNotificationUsersList(ctx, action);
  }

  // MF product
  @Action(TableActions.UpdateMFProductStatus)
  updateProductStatus(
    ctx: StateContext<TableStateModel>,
    action: TableActions.UpdateMFProductStatus
  ) {
    return this.mfHandler.updateStatus(ctx, action);
  }

  @Action(TableActions.SearchTBillProduct)
  searchTbillProduct(
    ctx: StateContext<TableStateModel>,
    action: TableActions.SearchTBillProduct
  ) {
    return this.lcmProductHandler.searchTbillProduct(ctx, action);
  }

  @Action(TableActions.SearchBondsProduct)
  searchBondsProduct(
    ctx: StateContext<TableStateModel>,
    action: TableActions.SearchBondsProduct
  ) {
    return this.lcmProductHandler.searchBondsProduct(ctx, action);
  }

  @Action(TableActions.SearchCPaperProduct)
  searchCPaperProduct(
    ctx: StateContext<TableStateModel>,
    action: TableActions.SearchCPaperProduct
  ) {
    return this.lcmProductHandler.searchCPaperProduct(ctx, action);
  }

  // transactions
  // wallet transactions
  @Action(TableActions.FilterWalletTransctionsList)
  filterWalletTransactionList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterWalletTransctionsList
  ) {
    return this.transactionsHandler.filterWalletTransactionList(ctx, action);
  }

  @Action(TableActions.GetProvidusBalance)
  getProvidusBalance(ctx: StateContext<TableStateModel>) {
    this.transactionsHandler.getProvidusBalance(ctx);
  }

  @Action(TableActions.GetAccessBankBalance)
  getAccessBankBalance(ctx: StateContext<TableStateModel>) {
    this.transactionsHandler.getAccessBankBalance(ctx);
  }

  @Action(TableActions.GetVertoBalance)
  getVertoBalance(ctx: StateContext<TableStateModel>) {
    this.transactionsHandler.getVertoBalance(ctx);
  }

  @Action(TableActions.GetPaystackBalance)
  getPaystackBalance(ctx: StateContext<TableStateModel>) {
    this.transactionsHandler.getPaystackBalance(ctx);
  }

  // fx transactions
  @Action(TableActions.FilterFxTransactionsList)
  filterFxTransactionList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterFxTransactionsList
  ) {
    return this.transactionsHandler.filterFxTransactionList(ctx, action);
  }

  @Action(TableActions.GetFxSummary)
  getFxSummary(ctx: StateContext<TableStateModel>) {
    this.transactionsHandler.getFxSummary(ctx);
  }

  // cash transactions
  @Action(TableActions.FilterCashTransctionsList)
  filterCashTransactionList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterCashTransctionsList
  ) {
    return this.transactionsHandler.filterCashTransactionList(ctx, action);
  }

  @Action(TableActions.GetInvestmentCashBalance)
  getInvestmentCashBalance(ctx: StateContext<TableStateModel>) {
    this.transactionsHandler.getInvestmentCashBalance(ctx);
  }

  @Action(TableActions.GetWalletCashBalance)
  getWalletCashBalance(ctx: StateContext<TableStateModel>) {
    this.transactionsHandler.getWalletCashBalance(ctx);
  }

  // referrals
  @Action(TableActions.FilterReferralList)
  filterReferralList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterReferralList
  ) {
    this.referralsHandler.filterReferralList(ctx, action);
  }

  @Action(TableActions.GetReferralsMetadata)
  getReferralMetadata(ctx: StateContext<TableStateModel>) {
    this.referralsHandler.handleGetMetadata(ctx);
  }

  // identity log
  @Action(TableActions.FilterIdentityLogList)
  filterIdentityLogList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterIdentityLogList
  ) {
    return this.identityHandler.filterIdentityLogList(ctx, action);
  }

  // update approvals
  @Action(TableActions.ApproveUpdate)
  approveUpdate(
    ctx: StateContext<TableStateModel>,
    action: TableActions.ApproveUpdate
  ) {
    return this.updateApprovalsHandler.approveUpdate(ctx, action);
  }

  @Action(TableActions.GetUpdatesMetadata)
  getUpdatesMetadata(ctx: StateContext<TableStateModel>) {
    this.updateApprovalsHandler.handleGetApprovalMetadata(ctx);
  }

  @Action(TableActions.FilterApprovalList)
  filterUpdateApprovalList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterApprovalList
  ) {
    return this.updateApprovalsHandler.filterUpdateApprovalList(ctx, action);
  }

  // audit log
  @Action(TableActions.FilterAuditLogList)
  filterAuditLogList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterAuditLogList
  ) {
    return this.auditHandler.filterAuditLogList(ctx, action);
  }

  // order approvals
  @Action(TableActions.ApproveOrRejectOrder)
  approveOrRejectOrder(
    ctx: StateContext<TableStateModel>,
    action: TableActions.ApproveOrRejectOrder
  ) {
    return this.orderApprovalsHandler.approveOrRejectOrder(ctx, action);
  }

  @Action(TableActions.FilterOrderApprovalList)
  filterOrderApprovalList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterOrderApprovalList
  ) {
    return this.orderApprovalsHandler.filterOrderApprovalList(ctx, action);
  }
}

export function getDefaultTableState<T>(): ITableStateModel<T> {
  return {
    data: [],
    loading: false,
    error: null,
    pagination: new Pagination(),
    totalCount: 0,
    filters: {},
    selectedItems: [],
  };
}
